import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/constants';

function HomeRatingsSection() {
  const [ratings, setRatings] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetch(`${API_ENDPOINTS.RATINGS}?limit=50`, { signal: controller.signal })
      .then((response) => response.ok ? response.json() : Promise.reject(new Error('Ratings unavailable')))
      .then((payload) => {
        const validRatings = payload.success && Array.isArray(payload.data)
          ? payload.data.filter((item) => item && item.name && item.event_name && Number.isInteger(item.rating) && item.rating >= 1 && item.rating <= 5)
          : [];
        setActiveIndex(0);
        setRatings(validRatings);
      })
      .catch((error) => { if (error.name !== 'AbortError') setRatings([]); });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (ratings.length < 2 || paused || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const interval = window.setInterval(() => setActiveIndex((current) => (current + 1) % ratings.length), 6000);
    return () => window.clearInterval(interval);
  }, [paused, ratings.length]);

  if (!ratings.length) return null;
  const activeRating = ratings[activeIndex] || ratings[0];
  const tags = String(activeRating.tags || '').split(';').map((tag) => tag.trim()).filter(Boolean);
  const move = (direction) => setActiveIndex((current) => (current + direction + ratings.length) % ratings.length);

  return (
    <section
      className="home-ratings band-tight"
      aria-labelledby="home-ratings-title"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setPaused(false); }}
    >
      <div className="wrap home-ratings-layout">
        <div className="home-ratings-heading">
          <span className="eyebrow">Community ratings</span>
          <h2 id="home-ratings-title">Built for people who lead with technology</h2>
          <p>What attendees say after learning, building, and connecting with us.</p>
        </div>

        <div className="home-ratings-stage" aria-live="polite">
          <article className="home-rating-card">
            <div className="home-rating-stars" aria-label={`${activeRating.rating} out of 5 stars`}>
              {Array.from({ length: 5 }, (_, index) => (
                <Star key={index} size={19} fill={index < activeRating.rating ? 'currentColor' : 'none'} aria-hidden="true" />
              ))}
            </div>
            <h3>{activeRating.event_name}</h3>
            {tags.length > 0 && <div className="home-rating-tags">{tags.map((tag) => <span key={tag}>{tag}</span>)}</div>}
            <footer><strong>{activeRating.name}</strong><span>{activeIndex + 1} / {ratings.length}</span></footer>
          </article>

          {ratings.length > 1 && (
            <div className="home-ratings-controls">
              <button type="button" onClick={() => move(-1)} aria-label="Previous rating" title="Previous rating"><ChevronLeft size={20} /></button>
              <div className="home-ratings-progress" aria-hidden="true"><span style={{ width: `${((activeIndex + 1) / ratings.length) * 100}%` }} /></div>
              <button type="button" onClick={() => move(1)} aria-label="Next rating" title="Next rating"><ChevronRight size={20} /></button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default HomeRatingsSection;